<template>
    <v-expansion-panels accordion flat v-model="currentOpenSettingsPanel" style="padding-right: 1px">
        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Identity')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <component-props-setting :config="$root.siteconfig.root" @save="delayedSave" />
            </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Font')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <text-style-setting :label="$translate('Heading') +  ' 1'" v-model="$root.siteconfig.fonts.h1" @input="delayedSave" />
                <text-style-setting :label="$translate('Heading') +  ' 2'" v-model="$root.siteconfig.fonts.h2" @input="delayedSave" />
                <text-style-setting :label="$translate('Heading') +  ' 3'" v-model="$root.siteconfig.fonts.h3" @input="delayedSave" />
                <text-style-setting :label="$translate('Heading') +  ' 4'" v-model="$root.siteconfig.fonts.h4" @input="delayedSave" />
                <text-style-setting :label="$translate('Heading') +  ' 5'" v-model="$root.siteconfig.fonts.h5" @input="delayedSave" />
                <text-style-setting :label="$translate('Heading') +  ' 6'" v-model="$root.siteconfig.fonts.h6" @input="delayedSave" />
                <text-style-setting :label="$translate('Paragraph text')" v-model="$root.siteconfig.fonts.p" @input="delayedSave" />
                <text-style-setting :label="$translate('Menu')" v-model="$root.siteconfig.fonts.menu" @input="delayedSave" />
            </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Standard pages')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <standard-page-settings />
            </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Component settings')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <div v-for="c in componentsWithGlobalSettings" :key="c.component.name">
                    <div class="d-flex">
                        <v-icon>{{c.component.zoezi.icon}}</v-icon>
                        <h3 class="ml-2">{{$translate(c.component.zoezi.title)}}</h3>
                    </div>
                    <component-setting v-for="prop in c.props" :key="prop.name" v-model="$root.siteconfig.componentDefaults[c.component.name][prop.name]" :prop="prop" @input="delayedSave" />
                </div>
            </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Own components')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <own-components-list />
            </v-expansion-panel-content>
        </v-expansion-panel>


        <v-expansion-panel>
            <v-expansion-panel-header>
                <h2>{{$translate('Advanced')}}</h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <v-switch v-model="$root.siteconfig.autoPublish" :label="$translate('Publish directly upon change')" @change="save" color="zoezi-edit-color"/>

                <v-text-field v-model="$root.siteconfig.domainname" :label="$translate('Domain name')" @input="delayedSave" color="zoezi-edit-color" />

                <vue-code-setting :value="$root.siteconfig.css" :label="$translate('Stylesheet (CSS)')" @input="saveCss" css-only />

                <vue-code-setting v-model="$root.siteconfig.headHtml" :label="$translate('HTML code that is inserted at the end of <head>')" @input="delayedSave" html-only :require-single-tag="false" />

                <redirect-setting v-model="$root.siteconfig.redirects" :label="$translate('Redirect list')" @input="delayedSave" v-if="$api.hasFlag('flag-redirects')" />
            </v-expansion-panel-content>
        </v-expansion-panel>

    </v-expansion-panels>
</template>

<script>
import ComponentSetting from './ComponentSetting.vue'
import ComponentPropsSetting from './ComponentPropsSetting.vue'
import TextStyleSetting from './TextStyleSetting.vue'
import OwnComponentsList from './OwnComponentsList.vue'
import components from '../component.js'
import VueCodeSetting from './VueCodeSetting.vue'
import RedirectSetting from './RedirectSetting.vue'
import StandardPageSettings from './StandardPageSettings.vue'
import {debounce, isFunction} from 'lodash';

export default {
    name: 'GeneralSettings',
    components: {
        ComponentSetting,
        ComponentPropsSetting,
        TextStyleSetting,
        OwnComponentsList,
        VueCodeSetting,
        StandardPageSettings,
        RedirectSetting
    },
    data: () => ({
        currentOpenSettingsPanel: 0
    }),
    watch: {
        componentsWithGlobalSettings: {
            immediate: true,
            handler(list) {
                let dirty = false;
                let defaults = this.$root.siteconfig.componentDefaults;
                if (!defaults) {
                    this.$root.siteconfig.componentDefaults = defaults = {};
                    dirty = true;
                }

                list.forEach(c => {
                    if (!defaults[c.component.name]) {
                        defaults[c.component.name] = {};
                        dirty = true;
                    }

                    c.props.forEach(prop => {
                        if (defaults[c.component.name][prop.name] === undefined) {
                            defaults[c.component.name][prop.name] = isFunction(prop.default) ? prop.default() : prop.default;
                            if (defaults[c.component.name][prop.name] !== undefined) {
                                dirty = true;
                            }
                        }
                    })
                })

                if (dirty) {
                    this.save();
                }
            }
        },
        '$root.siteconfig.componentDefaults': {
            deep: true,
            handler() {
                this.setDefaults();
            }
        }
    },
    computed: {
        componentsWithGlobalSettings() {
            return components.getAllGlobalSettings();
        },
        rootProps() {
            return components.getComponentProps(this.$root.siteconfig.root.component);
        },
    },
    methods: {
        save() {
            this.$root.saveHomePage();
        },
        delayedSave: debounce(function() { this.save(); }, 1000),
        saveTitle() {
            this.$root.setTitle();
            this.delayedSave();
        },
        saveFavicon() {
            document.getElementById('favicon').href = this.$root.siteconfig.favicon ? this.$api.getImageUrl(this.$root.siteconfig.favicon, 50) : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
            this.delayedSave();
        },
        saveFont() {
            this.$root.loadFont(this.$root.siteconfig.font);
            this.save();
        },
        saveSiteName() {
            let site = this.$root.sites.find(x => x.id == this.$root.homepage_id);
            if(site) {
                site.name = this.$root.siteconfig.root.props.name;
                site.basepath = this.$root.basepath;
            }
            this.delayedSave();
        },
        saveCss(css) {
            this.$root.siteconfig.css = css;
            this.delayedSave();
        },
        setDefaults() {
            components.setComponentDefaults(this.$root.siteconfig);
            this.componentsWithGlobalSettings.forEach(c => {
                let firstWrapper;
                let getLiveComponents = (componentName) => {
                    let getAll = (node, items) => {
                        if (!firstWrapper && node.$options.name == 'ComponentWrapper') {
                            this.firstWrapper = node;
                        }

                        node.$children.forEach((x) => getAll(x, items))

                        if (node.$options.name == componentName) {
                            items.push(node);
                        }
                    };

                    let items = [];
                    getAll(this.$root, items);
                    return items;
                };

                let components = getLiveComponents(c.component.name);
                components.forEach(c => {
                    while (c && c.$options.name != 'ComponentWrapper') {
                        c = c.$parent;
                    }

                    if (c && c.update) {
                        c.update();
                    }
                });
            })
        },
        saveRootProp(prop, value) {
            if (prop.name == 'colors') {
                this.$root.setThemeColors(value);
            }
            this.delayedSave();
        }
    }
}
</script>
